<template>
    <div data-testid="custom-checkbox">
        <v-row>
            <v-col cols="1" class="checkboxInputWrapper px-0 py-0">
                <input
                    :class="'checkboxInput ' + inputClass"
                    :v-model="value"
                    role="checkbox"
                    :data-meta-id="dataMetaId"
                    tabindex="0"
                    aria-hidden="false"
                    :aria-label="(required ? '* ' : ' ') + getAriaLabel"
                    type="checkbox"
                    :name="inputId"
                    :valid="(!required || value) ? 'true':'false'"
                    :id="inputId"
                    @change="(e) => { emitValue(e.target.checked) }"
                    @blur="this.onBlur"
                />
            </v-col>
            <v-col class="checkboxInputLabelWrapper">
                <label :for="inputId" :class="isRequired ? 'required' : ''">
                    {{ (required ? '* ' : ' ') }}
                    <span ref="label" v-html="label" />
                </label>
            </v-col>
        </v-row>
        <v-row v-if="isRequired && getFieldName !== ''">
            <v-col>
                <div class="v-messages theme--light error--text" :role="getRole">
                    <div class="v-messages__wrapper">
                        <div tabindex="0" aria-hidden="false" class="v-messages__message">
                            {{ $t('base.errors.requiredFieldWithLabel', {fieldLabel: getFieldName}) }}
                        </div>
                    </div>
                </div>
            </v-col>
        </v-row>
        <v-row class="visually-hidden">
            <div aria-live="assertive">
                <p v-if="value !== lastValue">
                    {{ getChangedValueAriaLabel }}
                </p>
            </div>
        </v-row>
    </div>
</template>

<script>
import { removeHTMLTags, haveLinkElement } from '@/utils.js';
import { inputHelpers } from '@/mixins/inputHelpers';
import { get } from 'lodash';
import { mapGetters } from 'vuex';

export default {
    name: 'custom-checkbox',
    props: {
        inputId: { type: String, required: true },
        dataMetaId: { type: Number, required: false },
        required: { type: Boolean, required: false, default: false },
        value: { type: Boolean, required: false, default: false },
        ariaLabel: { type: String, required: false, default: null },
        label: { type: String, required: true },
        inputClass: { type: String, required: false, default: '' }
    },
    mixins: [inputHelpers],
    data: () => {
        return {
            focused: false,
            lastValue: null
        };
    },
    mounted() {
        this.lastValue = this.value;
    },
    computed: {
        ...mapGetters({
            upperLocale: 'upperLocale',
            getBaseMetadataById: 'services/getBaseMetadataById',
        }),
        getAriaLabel() {
            return this.formattedAriaLabel(removeHTMLTags(this.ariaLabel ?? this.label), this.required);
        },
        getFieldName() {
            return get(this.getBaseMetadataById(this.dataMetaId), `description${this.upperLocale}`, this.getOtherFieldName);
        },
        getOtherFieldName() {
            if(this.inputId === 'tosAndGDPRAgreement') {
                return this.$i18n.t('inputValidationAccessibilityLang.terms_agreement');
            }

            if(this.inputId === 'consentCollectData') {
                return this.$i18n.t('inputValidationAccessibilityLang.privacy_policy_agreement');
            }

            if(this.inputId === 'delegatedPiConsent') {
                return this.$i18n.t('inputValidationAccessibilityLang.delegate_consent');
            }

            if(this.inputId === 'optinNewServiceList') {
                return this.$i18n.t('inputValidationAccessibilityLang.received_communication_consent');
            }
            return '';
        },
        getRole() {
            return haveLinkElement(this.$refs.label) ? undefined : 'alert';
        },
        isRequired() {
            return this.required && this.focused && !this.value;
        },
        getChangedValueAriaLabel() {
            const ariaLabelI18n = `form.checkbox.accessibility.changedValueAriaLabel${(this.value) ? '' : 'NotSelected'}`;
            const requiredOrOptionalI18n = (this.required) ? this.$i18n.t('form.required') : this.$i18n.t('form.optional');

            return this.$i18n.t(ariaLabelI18n, [ requiredOrOptionalI18n ]);
        }
    },
    methods: {
        emitValue(value) {
            this.lastValue = this.value;
            this.$emit('update:value', value);
        },
        onBlur() {
            if(this.focused) {
                return;
            }

            this.focused = true;
        }
    }
};
</script>
<style scoped lang="scss">
.checkboxInputWrapper {
    max-width: 25px !important;
    position: relative;
}
.checkboxInputLabelWrapper label{
    font-size: 16px;
}
.checkboxInput {
    accent-color: #00ccf2 !important;
    width: 25px;
    height:25px;
    margin: 0;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
}
.required {
    color: red !important;
}
</style>
